import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Save, Loader2 } from "lucide-react";
import { ownerApi } from "@/lib/api";
import { Spinner } from "@/components/common/States";
import { Switch } from "@/components/ui/switch";

const LABELS = { index: "Home", bookings: "Orders", games: "Games", assistant: "Assistant", profile: "Account", custom1: "Custom tab 1", custom2: "Custom tab 2" };
const input = "w-full rounded-xl border border-border bg-background p-2.5 text-sm outline-none focus:border-primary";

export default function OwnerTabs() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["ownerTabs"], queryFn: ownerApi.getTabs });
  const [items, setItems] = useState([]);

  useEffect(() => {
    const list = Array.isArray(query.data?.items) ? query.data.items : Array.isArray(query.data) ? query.data : [];
    if (list.length) setItems([...list].sort((a, b) => a.order - b.order));
  }, [query.data]);

  const move = (i, dir) => {
    setItems((arr) => {
      const next = [...arr];
      const j = i + dir;
      if (j < 0 || j >= next.length) return next;
      [next[i], next[j]] = [next[j], next[i]];
      return next.map((t, k) => ({ ...t, order: k }));
    });
  };
  const patch = (i, k, v) => setItems((arr) => arr.map((t, j) => (j === i ? { ...t, [k]: v } : t)));

  const save = useMutation({
    mutationFn: () => ownerApi.setTabs(items.map((t, k) => ({ ...t, order: k }))),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["ownerTabs"] }); queryClient.invalidateQueries({ queryKey: ["tabs"] }); toast.success("Tabs saved"); },
    onError: (e) => toast.error(e.message || "Could not save tabs"),
  });

  if (query.isPending) return <Spinner />;

  return (
    <div className="space-y-3 pb-20">
      <p className="text-sm text-muted-foreground">Show, hide and reorder the app&apos;s tabs. Enable up to 2 custom content tabs (title + image + text).</p>
      {items.map((t, i) => (
        <div key={t.key} data-testid={`owner-tab-row-${t.key}`} className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="font-display text-sm font-semibold">{LABELS[t.key] || t.key}</span>
              <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-muted-foreground">{t.kind}</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="rounded-lg bg-secondary p-1.5 disabled:opacity-40"><ArrowUp className="h-4 w-4" /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="rounded-lg bg-secondary p-1.5 disabled:opacity-40"><ArrowDown className="h-4 w-4" /></button>
              <Switch checked={!!t.visible} onCheckedChange={(v) => patch(i, "visible", v)} data-testid={`owner-tab-visible-${t.key}`} />
            </div>
          </div>
          {t.kind === "custom" && t.visible ? (
            <div className="mt-3 grid gap-2">
              <input className={input} value={t.title || ""} onChange={(e) => patch(i, "title", e.target.value)} placeholder="Tab title" />
              <input className={input} value={t.image_url || ""} onChange={(e) => patch(i, "image_url", e.target.value)} placeholder="Image URL (optional)" />
              <textarea className={input} rows={3} value={t.body || ""} onChange={(e) => patch(i, "body", e.target.value)} placeholder="Tab content text" />
            </div>
          ) : null}
        </div>
      ))}
      <button onClick={() => save.mutate()} disabled={save.isPending} data-testid="owner-tabs-save" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground disabled:opacity-60">
        {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save tabs
      </button>
    </div>
  );
}
